
import { Home, Users, Trophy, BookOpen, MessageSquare, Target, BarChart3, Play, Bot, Brain, Timer, Zap, Sparkles, Crown, ChevronRight, Info } from "lucide-react"
import { useState } from "react"
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { useAuth } from "@/hooks/useAuth"
import { usePrivateRooms } from "@/hooks/usePrivateRooms"
import { useChallengeSystem } from "@/hooks/useChallengeSystem"

interface AppSidebarProps {
  onStartGame?: (mode: 'classic' | 'hatch', opponent: string, isBot: boolean, elo?: number) => void;
  onStartEngine?: () => void;
  onStartMultiplayer?: () => void;
  onStartMatchmaking?: () => void;
  onShowBotChallenge?: () => void;
}

// Feature flags - set to false to hide features while preserving code
const PLAY_ONLINE_ENABLED = false;
const HATCH_MODE_ENABLED = true;
const COMMUNITY_ENABLED = false;
const CHAT_ZONE_ENABLED = false;
const GAME_RULES_ENABLED = false;
const SPEED_ZONES_ENABLED = false;
const TUTORIALS_ENABLED = false; // Hide broken tutorials
const PUZZLES_ENABLED = false; // Hide broken puzzles

// Navigation items
const navItems = [
  { title: "🏠 Home Base", url: "/", icon: Home },
  { title: "🤖 Challenge AI Squad", url: "/?section=ai-squad", icon: Bot, action: "bot-challenge", tourId: "ai-squad" },
  { title: "🧬 Hatch Evolution", url: "/?mode=hatch", icon: Sparkles, tourId: "hatch-evolution" },
  ...(TUTORIALS_ENABLED ? [{ title: "📖 Learn & Master", url: "/tutorials", icon: BookOpen }] : []),
  ...(PUZZLES_ENABLED ? [{ title: "🧩 Daily Puzzles", url: "/puzzles", icon: Target }] : []),
  { title: "📊 Game Analysis", url: "/analysis", icon: BarChart3 },
  { title: "🏆 Leaderboards", url: "/leaderboards", icon: Trophy, tourId: "leaderboard" },
  { title: "🔍 Find Players", url: "/players", icon: Users },
  { title: "ℹ️ About ChessHatch", url: "/about", icon: Info },
  // Hidden features - preserved for future activation
  ...(CHAT_ZONE_ENABLED ? [{ title: "💬 Chat Zone", url: "/forums", icon: MessageSquare }] : []),
  ...(COMMUNITY_ENABLED ? [{ title: "🌟 Community", url: "/community", icon: Users }] : []),
  ...(GAME_RULES_ENABLED ? [{ title: "📋 Game Rules", url: "/hatch-rules", icon: BookOpen }] : []),
]

// Complete AI Squad - All 8 bots from rookie to master
const quickBots = [
  { name: "Marty", elo: 400, difficulty: "Learning Chess", personality: "🌱" },
  { name: "Sophie", elo: 700, difficulty: "Getting Better", personality: "🚀" },
  { name: "Carlos", elo: 900, difficulty: "Solid Player", personality: "🎮" },
  { name: "Nina", elo: 1200, difficulty: "Club Level", personality: "♟️" },
  { name: "Jason", elo: 1400, difficulty: "Tactical Pro", personality: "👑" },
  { name: "Aisha", elo: 2000, difficulty: "Expert Level", personality: "⚡" },
  { name: "Viktor", elo: 2800, difficulty: "Grandmaster", personality: "⭐" },
  { name: "Magnus", elo: 3200, difficulty: "World Class", personality: "🏆" }
]

const timeControls = ["1+0", "3+0", "5+0", "10+0", "15+10", "30+0"]

export function AppSidebar({ onStartGame, onStartEngine, onStartMultiplayer, onStartMatchmaking, onShowBotChallenge }: AppSidebarProps) {
  const { state } = useSidebar()
  const { user } = useAuth()
  const { invitations } = usePrivateRooms()
  const { incomingChallenges } = useChallengeSystem()
  const [playOpen, setPlayOpen] = useState(false)
  const [botsOpen, setBotsOpen] = useState(false)
  const [hatchOpen, setHatchOpen] = useState(false)
  
  const challengeCount = incomingChallenges.length
  
  const showGameOptions = onStartGame && onStartEngine && onStartMultiplayer && onStartMatchmaking
  const collapsed = state === "collapsed"
  
  return (
    <Sidebar className="border-r border-sidebar-border bg-sidebar">
      <SidebarContent className="p-2">
        {/* Explore Zone */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs font-semibold px-2 mb-1 bg-gradient-to-r from-teen-cyan via-teen-pink to-teen-purple bg-clip-text text-transparent">✨ Explore</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {navItems.map((item) => {
                const showBadge = item.title === "🔍 Find Players" && user && challengeCount > 0;
                const tourId = (item as any).tourId;
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild size="sm">
                      {(item as any).action === "bot-challenge" && onShowBotChallenge ? (
                        <button 
                          onClick={onShowBotChallenge}
                          data-tour={tourId}
                          className="flex items-center gap-2 px-2 py-1.5 w-full text-left text-foreground/80 hover:text-foreground hover:bg-sidebar-accent rounded-md transition-all duration-200"
                        >
                          <item.icon className="w-4 h-4 text-teen-purple" />
                          {!collapsed && <span className="text-sm">{item.title}</span>}
                          {showBadge && !collapsed && (
                            <Badge variant="destructive" className="ml-auto h-5 w-5 flex items-center justify-center p-0 text-xs animate-pulse">
                              {challengeCount}
                            </Badge>
                          )}
                        </button>
                      ) : (
                        <a 
                          href={item.url} 
                          data-tour={tourId}
                          className="flex items-center gap-2 px-2 py-1.5 text-foreground/80 hover:text-foreground hover:bg-sidebar-accent rounded-md transition-all duration-200"
                        >
                          <item.icon className="w-4 h-4 text-teen-cyan" />
                          {!collapsed && <span className="text-sm">{item.title}</span>}
                          {showBadge && !collapsed && (
                            <Badge variant="destructive" className="ml-auto h-5 w-5 flex items-center justify-center p-0 text-xs animate-pulse">
                              {challengeCount}
                            </Badge>
                          )}
                        </a>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Game Options - Only show when callbacks are provided */}
        {showGameOptions && (
          <>
            {/* Quick Play */}
            <SidebarGroup>
              <Collapsible open={playOpen} onOpenChange={setPlayOpen}>
                <CollapsibleTrigger asChild>
                  <SidebarGroupLabel role="button" className="flex items-center justify-between px-2 py-1.5 text-xs font-semibold cursor-pointer hover:bg-sidebar-accent rounded-sm">
                    <span className="flex items-center gap-2">
                      <Play className="w-3 h-3 text-teen-green" />
                      {!collapsed && <span className="bg-gradient-to-r from-teen-green to-teen-blue bg-clip-text text-transparent">⚔️ Battle Arena</span>}
                    </span>
                    {!collapsed && <ChevronRight className={`w-3 h-3 transition-transform ${playOpen ? 'rotate-90' : ''}`} />}
                  </SidebarGroupLabel>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarGroupContent className="pl-2">
                    <div className="space-y-1">
                      {PLAY_ONLINE_ENABLED && (
                        <Button 
                          onClick={onStartMatchmaking}
                          size="sm" 
                          className="w-full justify-start h-8 text-xs bg-primary hover:bg-primary-hover"
                        >
                          <Users className="w-3 h-3 mr-2" />
                          {!collapsed && "🌍 Battle Online"}
                        </Button>
                      )}
                      <Button 
                        onClick={() => onStartGame('classic', 'Practice Bot', true, 400)}
                        variant="outline" 
                        size="sm" 
                        className="w-full justify-start h-8 text-xs"
                      >
                        <Bot className="w-3 h-3 mr-2" />
                        {!collapsed && "🤖 vs Bots"}
                      </Button>
                      <Button 
                        onClick={onStartEngine}
                        variant="outline" 
                        size="sm" 
                        className="w-full justify-start h-8 text-xs"
                      >
                        <Brain className="w-3 h-3 mr-2" />
                        {!collapsed && "🧠 Engine Beast"}
                      </Button>
                    </div>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </Collapsible>
            </SidebarGroup>

            {/* Quick Bots */}
            <SidebarGroup>
              <Collapsible open={botsOpen} onOpenChange={setBotsOpen}>
                <CollapsibleTrigger asChild>
                  <SidebarGroupLabel role="button" className="flex items-center justify-between px-2 py-1.5 text-xs font-semibold cursor-pointer hover:bg-sidebar-accent rounded-sm">
                    <span className="flex items-center gap-2">
                      <Bot className="w-3 h-3 text-teen-purple" />
                      {!collapsed && <span className="bg-gradient-to-r from-teen-purple to-teen-gold bg-clip-text text-transparent">🤖 AI Squad</span>}
                    </span>
                    {!collapsed && <ChevronRight className={`w-3 h-3 transition-transform ${botsOpen ? 'rotate-90' : ''}`} />}
                  </SidebarGroupLabel>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarGroupContent className="pl-2">
                    <div className="space-y-1">
                      {quickBots.map((bot) => (
                        <Button
                          key={bot.name}
                          variant="ghost"
                          size="sm"
                          className="w-full justify-between h-8 text-xs px-2"
                          onClick={() => onShowBotChallenge ? onShowBotChallenge() : onStartGame?.('classic', bot.name, true, bot.elo)}
                        >
                          <span className="truncate flex items-center gap-1">
                            {!collapsed ? `${bot.personality} ${bot.name}` : bot.personality}
                          </span>
                          {!collapsed && <Badge variant="outline" className="text-xs h-4 px-1 bg-teen-blue/10 text-teen-blue border-teen-blue/30">{bot.elo}</Badge>}
                        </Button>
                      ))}
                    </div>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </Collapsible>
            </SidebarGroup>


            {/* Time Controls - Compact grid */}
            {SPEED_ZONES_ENABLED && !collapsed && (
              <SidebarGroup>
                <SidebarGroupLabel className="text-xs font-semibold px-2 mb-1">
                  <Timer className="w-3 h-3 mr-2 inline text-teen-gold" />
                  <span className="bg-gradient-to-r from-teen-gold to-teen-green bg-clip-text text-transparent">⚡ Speed Zones</span>
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <div className="grid grid-cols-3 gap-1 px-2">
                    {timeControls.map((time) => (
                      <Button 
                        key={time} 
                        variant="outline" 
                        size="sm" 
                        className="h-7 text-[10px] px-1"
                      >
                        {time.includes('+') ? <><Zap className="w-2 h-2 mr-1" />{time}</> : time}
                      </Button>
                    ))}
                  </div>
                </SidebarGroupContent>
              </SidebarGroup>
            )}
          </>
        )}
      </SidebarContent>
    </Sidebar>
  )
}
